














































































































/* General */
import Vue from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxSettingsAutoResponderResponse from './components/mailbox-settings-auto-responder-response.vue'
import MailboxSettingsOfficeHoursTooltip from './components/mailbox-settings-office-hours-tooltip.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IAutoResponse,
  IItemForSelection,
  INameAndDescription,
  IOfficeHourDay,
} from '@/api/types/response.types'
import {
  IMailboxesInputCreateAutoResponse,
  IMailboxesInputUpdateAutoResponse,
} from '@/api/types/request.types'
import { IIDBoolean } from '@/types/data-objects/global'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default Vue.extend({
  name: 'MailboxSettingsAutoResponder',

  metaInfo(): any {
    return {
      title: `Auto-Responder - ${this.name}`,
    }
  },

  components: {
    MailboxSettingsLayout,
    MailboxSettingsAutoResponderResponse,
    MailboxSettingsOfficeHoursTooltip,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      RouteAssistant,
      isLoading: true,
      isSubmitting: false,
      showUseCaseTypeOptions: false,
      needsOfficeHours: false,
      editingID: null,
      name: '',
      autoResponseVars: [] as INameAndDescription[],
      autoResponses: [] as IAutoResponse[],
      autoResponseUseCaseTypes: [] as IItemForSelection[],
      upsertingAutoResponse: {} as IAutoResponse,
      useCaseTypeStatuses: [] as IIDBoolean[],
      officeHourDays: [] as IOfficeHourDay[],
      modalOpened: false,
      rules: {
        subject: [
          {
            required: true,
            trigger: 'submit',
            message: 'Subject is required',
          },
        ],
        message: [
          {
            required: true,
            trigger: 'submit',
            message: 'Message is required',
          },
        ],
      },
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    isEditing(): boolean {
      return this.editingID != null
    },
    hasAutoResponses(): boolean {
      return this.autoResponses.length > 0
    },
  },

  watch: {
    modalOpened(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.autoResponseForm_Subject.focus()
        })
      }
    },
  },

  async created() {
    this.isLoading = true
    await this.getAutoResponderInfo()
    this.isLoading = false
  },

  methods: {
    getAutoResponse(id: number) {
      return this.autoResponses.find(ar => ar.id === id)
    },
    resetAllUseCaseTypes() {
      this.setUseCaseTypes([])
    },
    setUseCaseTypes(useCaseTypeIDs: number[]) {
      const _useCaseTypeStatuses = [] as IIDBoolean[]
      this.autoResponseUseCaseTypes.forEach((a, idx) => {
        _useCaseTypeStatuses[idx] = {
          id: a.id,
          val: useCaseTypeIDs.includes(a.id),
        }
      })
      this.useCaseTypeStatuses = _useCaseTypeStatuses
    },
    async getAutoResponderInfo() {
      try {
        const response = await MailboxesAPI.get_AutoResponder(this.id)
        this.name = response.name
        this.autoResponseVars = response.listAutoResponseVars
        this.autoResponses = response.listAutoResponses
        this.autoResponseUseCaseTypes = response.listAutoResponseUseCaseTypes
        this.officeHourDays = response.listOfficeHourDays
        this.needsOfficeHours = response.needsOfficeHours
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async upsertResponse() {
      // @ts-ignore
      this.$refs.upsertResponseForm.validate((valid: boolean) => {
        if (valid) {
          this.isEditing ? this.updateAutoResponse() : this.createAutoResponse()
          this.closeModal()
        }
      })
    },
    setShowUseCaseTypeOptionsBasedOnSelectedTypes() {
      if (!this.useCaseTypeStatuses) {
        this.showUseCaseTypeOptions = false
      }
      this.showUseCaseTypeOptions =
        this.useCaseTypeStatuses.filter(u => !u.val).length > 0
    },
    setUseCaseTypesOnUpsertingAutoResponse() {
      this.upsertingAutoResponse.listUseCaseTypeIDs = this.useCaseTypeStatuses
        .filter(u => u.val)
        .map(u => u.id)
    },
    beginEditResponse(id: number): void {
      this.upsertingAutoResponse = this.getAutoResponse(id)
      this.modalOpened = true
      this.editingID = id
      this.setUseCaseTypes(this.upsertingAutoResponse.listUseCaseTypeIDs)
      this.setShowUseCaseTypeOptionsBasedOnSelectedTypes()
    },

    beginCreateResponse(): void {
      this.upsertingAutoResponse = {} as IAutoResponse
      this.modalOpened = true
      this.editingID = null

      const usedUseCaseTypeIDs = this.autoResponses
        .map(a => a.listUseCaseTypeIDs)
        .reduce((accumulator, value) => accumulator.concat(value), [])

      const unusedUseCaseTypeIDs = this.autoResponseUseCaseTypes
        .filter(ar => !usedUseCaseTypeIDs.includes(ar.id))
        .map(ar => ar.id)

      this.setUseCaseTypes(unusedUseCaseTypeIDs)
      this.setShowUseCaseTypeOptionsBasedOnSelectedTypes()
    },

    async removeResponse(id: number) {
      await this.$confirm(
        `Are you sure you want to delete this auto response?<br />
         This cannot be undone.`,
        {
          dangerouslyUseHTMLString: true,
          center: true,
          confirmButtonText: 'Yes, Delete',
          confirmButtonClass: 'el-button--strawberry',
          cancelButtonText: 'Cancel',
          cancelButtonClass: 'el-button--text',
          type: 'warning',
          title: `Delete Auto-Response`,
        },
      )
      await this.deleteAutoResponse(id)
    },

    closeModal(): void {
      this.modalOpened = false
      // Clear out statuses so the <el-switch doesn't show transitioning toggle upon next modal opening
      this.resetAllUseCaseTypes()
      // @ts-ignore
      this.$refs.upsertResponseForm.clearValidate()
    },

    async createAutoResponse() {
      try {
        this.isSubmitting = true
        this.setUseCaseTypesOnUpsertingAutoResponse()
        const data = this
          .upsertingAutoResponse as IMailboxesInputCreateAutoResponse
        const response = await MailboxesAPI.post_CreateAutoResponse(
          this.id,
          data,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getAutoResponderInfo()
        this.isSubmitting = false
      }
    },

    async updateAutoResponse() {
      try {
        this.isSubmitting = true
        this.setUseCaseTypesOnUpsertingAutoResponse()
        const data = this
          .upsertingAutoResponse as IMailboxesInputUpdateAutoResponse
        const response = await MailboxesAPI.put_UpdateAutoResponse(
          this.id,
          this.editingID,
          data,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getAutoResponderInfo()
        this.isSubmitting = false
      }
    },

    async deleteAutoResponse(id: number) {
      try {
        this.isSubmitting = true
        const response = await MailboxesAPI.delete_DeleteAutoResponse(
          this.id,
          id,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        await this.getAutoResponderInfo()
        this.isSubmitting = false
      }
    },
  },
})
